import { useMutation, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../../hooks/useApiHelper";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useNotification from "../../components/shared/Notifications/useNotifications";
import { IDefaultResponse } from "../../models/DefaultResponse";
import {
  IDamagePreviewResult,
  ITitleConfigRequest,
  ITitleConfigResponse,
  ITitlePreviewExampleData,
  ITitlePreviewResult,
  TitleType,
} from "../../models/Category/TitleConfig";
import { TitleConfigQueryKeys } from "./keys";

export default function useTitleConfigMutations() {
  const { put, post, del } = useApiHelper();
  const queryClient = useQueryClient();
  const { saveHandlers } = useDefaultCRUDHandlers("Title configuration");
  const { addNotification } = useNotification();

  /** Seeds default configurations so the categories appear in the list. */
  const create = useMutation({
    mutationFn: (partCategoryIds: string[]) =>
      post<IDefaultResponse<ITitleConfigResponse[]>>(
        `/part/categories/title-config`,
        { partCategoryIds },
      ),
    onSuccess: (res) => {
      addNotification({
        variant: "success",
        primaryText: res.message || "Categories added",
      });
      queryClient.invalidateQueries({ queryKey: TitleConfigQueryKeys.all() });
    },
    onError: (error: Error) => {
      addNotification({
        variant: "error",
        primaryText: error.message || "Failed to add categories",
      });
    },
  });

  const remove = useMutation({
    mutationFn: (partCategoryId: string) =>
      del<IDefaultResponse<null>>(
        `/part/categories/${partCategoryId}/title-config`,
      ),
    onSuccess: () => {
      addNotification({
        variant: "success",
        primaryText: "Title configuration removed",
      });
      queryClient.invalidateQueries({ queryKey: TitleConfigQueryKeys.all() });
    },
    onError: (error: Error) => {
      addNotification({
        variant: "error",
        primaryText: error.message || "Failed to remove the configuration",
      });
    },
  });

  const save = useMutation({
    mutationFn: ({
      partCategoryId,
      config,
    }: {
      partCategoryId: string;
      config: ITitleConfigRequest;
    }) =>
      put<IDefaultResponse<ITitleConfigResponse>>(
        `/part/categories/${partCategoryId}/title-config`,
        config,
      ),
    onSuccess: (_res, variables) => {
      saveHandlers.onSuccess();
      queryClient.invalidateQueries({
        queryKey: TitleConfigQueryKeys.single(variables.partCategoryId),
      });
      // The picker shows a "Configured" marker and the title type, so it goes stale too.
      queryClient.invalidateQueries({ queryKey: TitleConfigQueryKeys.all() });
    },
    onError: (error: Error) => {
      addNotification({
        variant: "error",
        primaryText: error.message || "Failed to save title configuration",
      });
    },
  });

  /**
   * Returns the default configuration for a title type without saving it. The user still has to
   * press Save, so a mis-click on Reset is recoverable.
   */
  const resetToDefault = useMutation({
    mutationFn: ({
      partCategoryId,
      titleType,
    }: {
      partCategoryId: string;
      titleType: TitleType;
    }) =>
      post<IDefaultResponse<ITitleConfigResponse>>(
        `/part/categories/${partCategoryId}/title-config/reset?titleType=${titleType}`,
        {},
      ),
  });

  const duplicate = useMutation({
    mutationFn: ({
      sourcePartCategoryId,
      targetPartCategoryIds,
    }: {
      sourcePartCategoryId: string;
      targetPartCategoryIds: string[];
    }) =>
      post<IDefaultResponse<null>>(`/part/categories/title-config/duplicate`, {
        sourcePartCategoryId,
        targetPartCategoryIds,
      }),
    onSuccess: (res) => {
      addNotification({
        variant: "success",
        primaryText: res.message || "Configuration copied",
      });
      queryClient.invalidateQueries({ queryKey: TitleConfigQueryKeys.all() });
    },
    onError: (error: Error) => {
      addNotification({
        variant: "error",
        primaryText: error.message || "Failed to copy the configuration",
      });
    },
  });

  /**
   * Rebuilds every saved prompt from its stored settings after a change to the prompt rules,
   * instead of re-saving each category. Customers get the changed ones on the next category sync.
   */
  const recompileAll = useMutation({
    mutationFn: () =>
      post<IDefaultResponse<null>>(`/part/categories/title-config/recompile`, {}),
    onSuccess: (res) => {
      addNotification({
        variant: "success",
        primaryText: res.message || "Prompts rebuilt",
      });
      queryClient.invalidateQueries({ queryKey: TitleConfigQueryKeys.all() });
    },
    onError: (error: Error) => {
      addNotification({
        variant: "error",
        primaryText: error.message || "Failed to rebuild the prompts",
      });
    },
  });

  // Preview costs a real OpenAI call per press, so it is a deliberate button rather than a
  // live-on-change effect.
  const previewTitle = useMutation({
    mutationFn: ({
      partCategoryId,
      config,
      exampleData,
    }: {
      partCategoryId: string;
      config: ITitleConfigRequest;
      exampleData: ITitlePreviewExampleData;
    }) =>
      post<IDefaultResponse<ITitlePreviewResult>>(
        `/part/categories/${partCategoryId}/title-config/preview`,
        { config, exampleData },
      ),
    onError: (error: Error) => {
      addNotification({
        variant: "error",
        primaryText: error.message || "Failed to generate a preview title",
      });
    },
  });

  const previewDamage = useMutation({
    mutationFn: ({
      partCategoryId,
      config,
      imageUrls,
    }: {
      partCategoryId: string;
      config: ITitleConfigRequest;
      imageUrls?: string[];
    }) =>
      post<IDefaultResponse<IDamagePreviewResult>>(
        `/part/categories/${partCategoryId}/title-config/damage-preview`,
        { config, imageUrls: imageUrls ?? [] },
      ),
    onError: (error: Error) => {
      addNotification({
        variant: "error",
        primaryText:
          error.message || "Failed to generate a condition description",
      });
    },
  });

  return {
    create,
    remove,
    save,
    resetToDefault,
    duplicate,
    recompileAll,
    previewTitle,
    previewDamage,
  };
}
